import { Mail, Link2, Wallet, ShieldAlert, ShieldCheck } from "lucide-react";

/**
 * AP-5B.3 — Auth-method strip on `/account`.
 *
 * Read-only badge row that surfaces how the user established their
 * AssosPay session and the verification state of their declared
 * wallet. Pure presentational component — no state, no RPC, no API.
 *
 * Inputs:
 *   - authMethods: string[] from /api/auth/me ("native_magic_link" |
 *     "assos365"). May contain both when a user has linked twice.
 *   - walletConnected: boolean from /api/wallet/me
 *   - verificationStatus: "declared" | "verified" | null from /api/wallet/me
 *
 * Strict scope:
 *   * NO password / signature / SIWE prompt
 *   * NO env-var reads
 *   * NO localStorage / sessionStorage writes
 */

const NATIVE = "native_magic_link";
const ASSOS365 = "assos365";

function Badge({ icon: Icon, label, hint, kind, testid }) {
  const palette = {
    cyan: "border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[#00F0FF]",
    green: "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]",
    amber: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
    muted: "border-white/15 bg-white/5 text-white/65",
  }[kind || "muted"];
  return (
    <div
      data-testid={testid}
      className={`flex items-start gap-2 rounded-md border px-3 py-2 ${palette}`}
    >
      <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <div className="leading-tight">
        <p className="font-display text-[10px] uppercase tracking-[0.2em]">
          {label}
        </p>
        {hint && (
          <p className="mt-0.5 font-mono text-[10px] text-white/55">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

export default function AccountAuthStrip({
  authMethods,
  walletConnected,
  verificationStatus,
}) {
  const methods = Array.isArray(authMethods) ? authMethods : [];
  const hasNative = methods.includes(NATIVE);
  const hasAssos365 = methods.includes(ASSOS365);
  const declared =
    walletConnected && verificationStatus === "declared";
  const verified =
    walletConnected && verificationStatus === "verified";

  return (
    <div
      data-testid="account-auth-strip"
      data-has-native={hasNative ? "true" : "false"}
      data-has-assos365={hasAssos365 ? "true" : "false"}
      data-wallet-state={
        verified ? "verified" : declared ? "declared" : "none"
      }
      className="rounded-md border border-white/10 bg-black/40 px-4 py-3"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
        Account state
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {hasNative && (
          <Badge
            icon={Mail}
            label="Native email account"
            hint="Magic-link sign-in · no password"
            kind="cyan"
            testid="account-auth-badge-native"
          />
        )}
        {hasAssos365 && (
          <Badge
            icon={Link2}
            label="Assos365 linked"
            hint="Partner deep-link session active"
            kind="green"
            testid="account-auth-badge-assos365"
          />
        )}
        {!hasNative && !hasAssos365 && (
          <Badge
            icon={ShieldCheck}
            label="Session active"
            hint="Auth method unknown"
            kind="muted"
            testid="account-auth-badge-unknown"
          />
        )}
        {declared && (
          <Badge
            icon={ShieldAlert}
            label="Wallet declared · not verified"
            hint="Cryptographic verification ships in a future release"
            kind="amber"
            testid="account-auth-badge-wallet-declared"
          />
        )}
        {verified && (
          <Badge
            icon={Wallet}
            label="Wallet verified"
            hint="Cryptographically proven via wallet signature"
            kind="green"
            testid="account-auth-badge-wallet-verified"
          />
        )}
      </div>
    </div>
  );
}
